import React, { useState, useEffect, useCallback } from "react";
import { InputField } from "../../components/_input/_input";
import { Tab, Tabs, TabList, TabPanel } from "react-tabs";
import "react-tabs/style/react-tabs.css";
import { toast } from "react-toastify";
import axios from "axios";
import config from '../../configuration/config';
import SectionHeading from "../../components/sectionHeading";
import crypto from "crypto-js";

export default function DeleteAccount({ trans, user }) {
    const ACCESS_TYPES = { EMAIL: "Email", PHONE: "PhoneNumber" };
    const [accessType, setAccessType] = useState(ACCESS_TYPES.EMAIL);
    const [request, setRequest] = useState({ password: "", validationKey: "" });
    const [isLoading, setIsLoading] = useState(false);
    const [timer, setTimer] = useState(0);
    const [confirmPhone, setConfirmPhone] = useState(false);

    // Countdown timer for re-sending code
    useEffect(() => {
        if (timer <= 0) {
            setConfirmPhone(false);
            return;
        }

        const interval = setInterval(() => setTimer(t => t - 1), 1000);
        return () => clearInterval(interval);
    }, [timer]);

    // Send verification code to phone
    const handleSendCode = async () => {
        if (!user?.phone) return toast.error(trans("error2:no_phone_number_error"));

        setIsLoading(true);
        try {
            const { data } = await axios.post(`${config.controllURL}api/Administration/SendSms`, {
                AccessType: ACCESS_TYPES.PHONE,
                Access: user.phone
            });

            if (!data) {
                setConfirmPhone(false);
                toast.error(trans("common:failed-access"));
            } else {
                setConfirmPhone(true);
                setTimer(60);
            }
        } catch (err) {
            toast.error(trans("common:failed-access"));
            console.error(err);
        } finally {
            setIsLoading(false);
        }
    };

    // Delete account logic
    const handleDeleteAccount = useCallback(async () => {
        const isPhoneInvalid = accessType === ACCESS_TYPES.PHONE && (!user?.phone || !request.validationKey);
        const isEmailInvalid = accessType === ACCESS_TYPES.EMAIL && (!user?.email || !request.password);

        if (isPhoneInvalid || isEmailInvalid) {
            toast.error(trans("common:failed-access"));
            return;
        }

        if (!window.confirm(trans("user:confirmDeactivateAccount"))) return;

        setIsLoading(true);
        try {
            const payload = {
                access: accessType === ACCESS_TYPES.EMAIL ? user.email : user.phone,
                accessType,
                password: accessType === ACCESS_TYPES.EMAIL ? crypto.MD5(request.password).toString() : undefined,
                validationKey: accessType === ACCESS_TYPES.PHONE ? request.validationKey : undefined,
            };

            const res = await axios.post(`${config.controllURL}api/Administration/DeleteAccount`, payload);

            if (res.status !== 200) throw new Error("Failed");
            deleteAllCookies();
        } catch (err) {
            toast.error(trans("user:accountDeactivationFailed"));
            console.error(err);
        } finally {
            setIsLoading(false);
        }
    }, [accessType, request, user, trans]);

    // Clear cookies and redirect
    const deleteAllCookies = () => {
        document.cookie.split(";").forEach(c => document.cookie = c.split("=")[0] + "=;max-age=-1");
        window.localStorage.clear();
        window.location.href = "/";
    };

    return (
        <div className="nice-container-fluid">
            <Tabs
                selectedIndex={accessType === ACCESS_TYPES.EMAIL ? 0 : 1}
                onSelect={index => {
                    setAccessType(index === 0 ? ACCESS_TYPES.EMAIL : ACCESS_TYPES.PHONE);
                    setRequest({ password: "", validationKey: "" });
                    setConfirmPhone(false);
                }}
            >
                <TabList className="react-tabs__tab-list flex text-20 gap-4 mb-4">
                    <Tab className="react-tabs__tab text-size-16r">{trans("form:email")}</Tab>
                    <Tab className="react-tabs__tab text-size-16r">{trans("form:phone")}</Tab>
                </TabList>

                {/* Email Tab */}
                <TabPanel>
                    <SectionHeading hasRuler={false}>
                        <div className="nice-col-md-6 my-5">
                            <InputField
                                value={request.password}
                                name="password"
                                type="password"
                                onChangeValue={e => setRequest(prev => ({ ...prev, password: e.target.value }))}
                                title={trans("form:password")}
                            />
                        </div>
                        <div className="nice-col-md-6 my-5">
                            <button
                                className="v7-btn-danger w-full px-5 py-3"
                                onClick={handleDeleteAccount}
                                disabled={isLoading || !request.password}
                            >
                                {isLoading ? trans("common:loading") : trans("user:deleteConfirmation")}
                            </button>
                        </div>
                    </SectionHeading>
                </TabPanel>

                {/* Phone Tab */}
                <TabPanel>
                    <SectionHeading hasRuler={false}>
                        {confirmPhone ? (
                            <div className="nice-card p-4 rounded-xl">
                                <p className="nice-col-md-6 my-5 text-18 text-gray-600">
                                    {trans("user:verificationCodeSent")}
                                    <strong className="mb-4 text-20 text-gray-600"> {user.phone}</strong>
                                </p>
                                <div className="nice-col-md-6 my-5">
                                    <InputField
                                        value={request.validationKey}
                                        onChangeValue={e => setRequest(prev => ({ ...prev, validationKey: e.target.value }))}
                                        title={trans("user:verificationCode ")}
                                    />
                                </div>
                                <div className="nice-col-md-6 my-5">
                                    <button
                                        className="v7-btn-danger w-full px-5 py-3"
                                        onClick={handleDeleteAccount}
                                        disabled={isLoading || !request.validationKey}
                                    >
                                        {isLoading ? trans("common:loading") : trans("user:deleteConfirmation")}
                                    </button>
                                </div>
                                <button
                                    className={`mt-3 nice-col-md-6 text-sm text-18 underline ${timer > 0 ? "text-gray-400 cursor-not-allowed" : "text-blue-600"}`}
                                    onClick={handleSendCode}
                                    disabled={timer > 0}
                                >
                                    {timer > 0 ? `${trans("user:resendCode")} (${timer})` : trans("user:resendCode")}
                                </button>
                            </div>
                        ) : (
                            <div>
                                <p className="nice-col-md-6 my-5 text-18 text-gray-600">
                                    {trans("user:sendVerificationCodeToMobile")}
                                    <strong className="mb-4 text-20 text-gray-600"> {user.phone}</strong>
                                </p>
                                <div className="nice-col-md-6 my-5">
                                    <button
                                        className="v7-btn-primary w-full px-5 py-3"
                                        onClick={handleSendCode}
                                        disabled={isLoading}
                                    >
                                        {isLoading ? trans("common:loading") : trans("user:sendVerificationCode")}
                                    </button>
                                </div>
                            </div>
                        )}
                    </SectionHeading>
                </TabPanel>
            </Tabs>
        </div>
    );
}
